import React, { useEffect, useState } from 'react';
import Select from 'react-select';
import Swal from 'sweetalert2';
import dayjs from 'dayjs';
import http from 'services/http';
import { alerta } from 'components/Alert/index';
import Pagination from 'components/Paginacao';
import 'components/Paginacao/style.css';

const pageSize = 20; // mesmo valor do backend (paginate(20))

const MODALIDADES = [
    { chave: 'milhar', label: 'Milhar' },
    { chave: 'centena', label: 'Centena' },
    { chave: 'dezena', label: 'Dezena' },
    { chave: 'grupo', label: 'Grupo' },
];

const situacaoBadge = {
    ativa: 'badge-primary',
    agendada: 'badge-secondary',
    encerrada: 'badge-secondary',
    cancelada: 'badge-dark',
};

const situacaoLabel = {
    ativa: 'Ativa',
    agendada: 'Agendada',
    encerrada: 'Encerrada',
    cancelada: 'Cancelada',
};

export default function Promocoes() {
    const [dataInicio, setDataInicio] = useState('');
    const [dataFim, setDataFim] = useState('');
    const [opcoesGerentes, setOpcoesGerentes] = useState([]);
    const [gerentesSelecionados, setGerentesSelecionados] = useState([]);
    const [opcoesExtracoes, setOpcoesExtracoes] = useState([]);
    const [extracoesSelecionadas, setExtracoesSelecionadas] = useState([]);
    const [modalidades, setModalidades] = useState({
        milhar: { marcado: false, valor: '' },
        centena: { marcado: false, valor: '' },
        dezena: { marcado: false, valor: '' },
        grupo: { marcado: false, valor: '' },
    });
    const [salvando, setSalvando] = useState(false);

    const [promocoes, setPromocoes] = useState([]);
    const [total, setTotal] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const carregarGerentes = async () => {
            try {
                const { data: { data: gerentes } } = await http.get('/usuarios', {
                    params: { nivel: 'gerente', resumida: true },
                });
                setOpcoesGerentes(
                    (gerentes ?? []).map((g) => ({ value: g.id, label: (g.nome ?? g.login ?? '').toUpperCase() }))
                );
            } catch (e) {
                alerta(e.response?.data ?? { success: false, message: 'Erro ao carregar gerentes.' });
            }
        };

        const carregarExtracoes = async () => {
            try {
                const { data: { data: loterias } } = await http.get('/loterias', {params: {todas: 1}});
                setOpcoesExtracoes(
                    (loterias ?? []).map((l) => ({ value: l.id, label: l.nome }))
                );
            } catch (e) {
                alerta(e.response?.data ?? { success: false, message: 'Erro ao carregar extrações.' });
            }
        };

        // independentes — disparadas em paralelo, sem uma esperar a outra
        carregarGerentes();
        carregarExtracoes();
    }, []);

    useEffect(() => {
        let ativo = true;
        const carregar = async () => {
            setLoading(true);
            try {
                const { data: { data: paginator } } = await http.get('/promocoes', {
                    params: { page: currentPage },
                });
                if (!ativo) return;
                setPromocoes(paginator.data ?? []);
                setTotal(paginator.total ?? 0);
            } catch (e) {
                if (ativo) alerta(e.response?.data ?? { success: false, message: 'Erro ao listar promoções.' });
            } finally {
                if (ativo) setLoading(false);
            }
        };
        carregar();
        return () => { ativo = false; };
    }, [currentPage]);

    const recarregar = () => {
        http.get('/promocoes', { params: { page: currentPage } })
            .then(({ data: { data: paginator } }) => {
                setPromocoes(paginator.data ?? []);
                setTotal(paginator.total ?? 0);
            })
            .catch((e) => alerta(e.response?.data ?? { success: false, message: 'Erro ao listar promoções.' }));
    };

    const marcarModalidade = (chave, marcado) => {
        setModalidades((atual) => ({
            ...atual,
            [chave]: { marcado, valor: marcado ? atual[chave].valor : '' },
        }));
    };

    const alterarValorModalidade = (chave, valor) => {
        setModalidades((atual) => ({ ...atual, [chave]: { ...atual[chave], valor } }));
    };

    const limparFormulario = () => {
        setDataInicio('');
        setDataFim('');
        setGerentesSelecionados([]);
        setExtracoesSelecionadas([]);
        setModalidades({
            milhar: { marcado: false, valor: '' },
            centena: { marcado: false, valor: '' },
            dezena: { marcado: false, valor: '' },
            grupo: { marcado: false, valor: '' },
        });
    };

    const criarPromocao = async () => {
        if (!dataInicio || !dataFim) {
            alerta({ success: false, message: 'Informe o período da promoção!' });
            return;
        }

        if (gerentesSelecionados.length === 0) {
            alerta({ success: false, message: 'Selecione ao menos um gerente!' });
            return;
        }

        if (extracoesSelecionadas.length === 0) {
            alerta({ success: false, message: 'Selecione ao menos uma extração!' });
            return;
        }

        const modalidadesPayload = {};
        MODALIDADES.forEach(({ chave }) => {
            if (modalidades[chave].marcado && modalidades[chave].valor) {
                modalidadesPayload[chave] = Number(modalidades[chave].valor);
            }
        });

        if (Object.keys(modalidadesPayload).length === 0) {
            alerta({ success: false, message: 'Marque ao menos uma modalidade com valor!' });
            return;
        }

        setSalvando(true);
        try {
            const response = await http.post('/promocoes', {
                data_inicio: dataInicio,
                data_fim: dataFim,
                gerentes: gerentesSelecionados.map((g) => g.value),
                extracoes: extracoesSelecionadas.map((e) => e.value),
                modalidades: modalidadesPayload,
            });
            alerta(response.data);
            limparFormulario();
            setCurrentPage(1);
            recarregar();
        } catch (e) {
            alerta(e.response?.data ?? { success: false, message: 'Erro ao criar promoção.' });
        } finally {
            setSalvando(false);
        }
    };

    const cancelarPromocao = (promocao) => {
        Swal.fire({
            title: 'Cancelar promoção?',
            text: 'A cotação normal volta a valer imediatamente para novas apostas.',
            type: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            reverseButtons: true,
            confirmButtonText: 'Sim, cancelar',
            cancelButtonText: 'Voltar',
        }).then(async (result) => {
            if (!result.value) return;
            try {
                const response = await http.patch(`/promocoes/${promocao.id}/cancelar`);
                alerta(response.data);
                recarregar();
            } catch (e) {
                alerta(e.response?.data ?? { success: false, message: 'Erro ao cancelar promoção.' });
            }
        });
    };

    const mudarPagina = (page) => {
        setCurrentPage(page);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    return (
        <>
            <div className="titulo-principal">
                <span>Promoções</span>
            </div>

            <div className="conteudo-principal">
                <div className="bordered m-b">
                    <div className="btn-group caixa-inputs p-b">
                        <div className="input-criacao-usuario margin-vertical-30">
                            <label className="titulo-input-criacao">Início:</label>
                            <input
                                type="date"
                                className="form-control input-dados-criar"
                                value={dataInicio}
                                onChange={(e) => setDataInicio(e.target.value)}
                            />
                        </div>

                        <div className="input-criacao-usuario margin-vertical-30">
                            <label className="titulo-input-criacao">Fim:</label>
                            <input
                                type="date"
                                className="form-control input-dados-criar"
                                value={dataFim}
                                onChange={(e) => setDataFim(e.target.value)}
                            />
                        </div>
                    </div>

                    <div className="btn-group caixa-inputs p-b">
                        <div className="input-criacao-usuario margin-vertical-30">
                            <label className="titulo-input-criacao">Gerentes:</label>
                            <div style={{ flexBasis: 270, flexGrow: 1, maxWidth: 520 }}>
                                <Select
                                    isMulti
                                    placeholder="Selecione os gerentes..."
                                    noOptionsMessage={() => 'Nenhum gerente'}
                                    options={opcoesGerentes}
                                    value={gerentesSelecionados}
                                    onChange={(selecionados) => setGerentesSelecionados(selecionados || [])}
                                />
                            </div>
                        </div>

                        <div className="input-criacao-usuario margin-vertical-30">
                            <label className="titulo-input-criacao">Extrações:</label>
                            <div style={{ flexBasis: 270, flexGrow: 1, maxWidth: 520 }}>
                                <Select
                                    isMulti
                                    placeholder="Selecione as extrações..."
                                    noOptionsMessage={() => 'Nenhuma extração'}
                                    options={opcoesExtracoes}
                                    value={extracoesSelecionadas}
                                    onChange={(selecionados) => setExtracoesSelecionadas(selecionados || [])}
                                />
                            </div>
                        </div>
                    </div>

                    <div className="p-b" style={{ padding: '0 10px' }}>
                        <label className="titulo-input-criacao d-block m-b">Modalidades promocionais:</label>
                        {MODALIDADES.map(({ chave, label }) => (
                            <div key={chave} className="d-flex align-items-center m-b" style={{ gap: '10px' }}>
                                <div className="form-check" style={{ width: 140 }}>
                                    <input
                                        type="checkbox"
                                        className="form-check-input"
                                        id={`modalidade-${chave}`}
                                        checked={modalidades[chave].marcado}
                                        onChange={(e) => marcarModalidade(chave, e.target.checked)}
                                    />
                                    <label className="form-check-label" htmlFor={`modalidade-${chave}`}>
                                        {label}
                                    </label>
                                </div>
                                <input
                                    type="number"
                                    min="1"
                                    className="form-control"
                                    style={{ maxWidth: 200 }}
                                    placeholder="Valor promocional"
                                    disabled={!modalidades[chave].marcado}
                                    value={modalidades[chave].valor}
                                    onChange={(e) => alterarValorModalidade(chave, e.target.value)}
                                />
                            </div>
                        ))}
                    </div>

                    <div className="d-flex m-b m-t" style={{ padding: '0 10px', justifyContent: 'left' }}>
                        <button
                            type="button"
                            style={{ display: 'block', width: '270px', marginLeft: '150px' }}
                            className="btn btn-success"
                            disabled={salvando}
                            onClick={criarPromocao}
                        >
                            {salvando ? 'Salvando...' : 'Criar Promoção'}
                        </button>
                    </div>
                </div>

                {loading ? (
                    <div className="p-4 text-center">Carregando...</div>
                ) : (
                    <table className="table table-sm text-center border mb-0 align-middle">
                        <thead className="thead-light">
                            <tr>
                                <th>Data</th>
                                <th>Gerente</th>
                                <th>Extrações</th>
                                <th>Milhar</th>
                                <th>Centena</th>
                                <th>Dezena</th>
                                <th>Grupo</th>
                                <th>Ativos/Inativos</th>
                                <th>Situação</th>
                                <th>Cancelar</th>
                            </tr>
                        </thead>
                        <tbody>
                            {promocoes.length === 0 ? (
                                <tr>
                                    <td colSpan="10" className="text-muted p-4">Nenhuma promoção cadastrada.</td>
                                </tr>
                            ) : promocoes.map((p) => (
                                <tr key={p.id}>
                                    <td style={{ whiteSpace: 'nowrap' }}>
                                        {dayjs(p.data_inicio).format('DD/MM/YYYY')} até {dayjs(p.data_fim).format('DD/MM/YYYY')}
                                    </td>
                                    <td className="text-left" style={{ whiteSpace: 'normal' }}>
                                        {(p.gerentes ?? []).length === 0
                                            ? <span className="text-muted">—</span>
                                            : p.gerentes.map((g) => g.nome ?? g.login).join(', ')}
                                    </td>
                                    <td className="text-left" style={{ whiteSpace: 'normal' }}>
                                        {(p.loterias ?? []).length === 0
                                            ? <span className="text-muted">—</span>
                                            : p.loterias.map((l) => l.nome).join(', ')}
                                    </td>
                                    <td>{p.modalidades?.milhar ?? '–'}</td>
                                    <td>{p.modalidades?.centena ?? '–'}</td>
                                    <td>{p.modalidades?.dezena ?? '–'}</td>
                                    <td>{p.modalidades?.grupo ?? '–'}</td>
                                    <td>
                                        <span className={`badge ${p.ativa ? 'badge-success' : 'badge-danger'}`}>
                                            {p.ativa ? 'Ativo' : 'Inativo'}
                                        </span>
                                    </td>
                                    <td>
                                        <span className={`badge ${situacaoBadge[p.situacao]}`}>
                                            {situacaoLabel[p.situacao]}
                                        </span>
                                    </td>
                                    <td>
                                        <button
                                            type="button"
                                            className="btn btn-sm btn-outline-danger"
                                            disabled={!['ativa', 'agendada'].includes(p.situacao)}
                                            onClick={() => cancelarPromocao(p)}
                                        >
                                            <i className="fa fa-trash" />
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}

                {!loading && total > pageSize && (
                    <div className="d-flex justify-content-center py-2">
                        <Pagination
                            currentPage={currentPage}
                            totalCount={total}
                            pageSize={pageSize}
                            onPageChange={mudarPagina}
                        />
                    </div>
                )}
            </div>
        </>
    );
}
